import { useState, useEffect, useCallback } from "react";
import { getThemes } from "./brandApi";

const cache = new Map();     // email -> themes array
const inflight = new Map();  // email -> in-flight promise

const listeners = new Set();

const emit = (email) => listeners.forEach((fn) => fn(email));

// Refetch and tell every mounted consumer. Call after create/update/delete
// so the create flow and the carousel see the change immediately.
export const refreshThemes = async (email) => {
  if (!email) return;
  inflight.delete(email);
  try {
    const data = await getThemes(email);
    cache.set(email, data);
  } catch (err) {
    console.error("Failed to refresh themes:", err);
    cache.delete(email);
  }
  emit(email);
};

export default function useThemes(email) {
  const [themes, setThemes] = useState(() => cache.get(email) || []);
  const [loading, setLoading] = useState(!cache.has(email));
  const [error, setError] = useState("");

  const load = useCallback(async (force = false) => {
    if (!email) {
      setThemes([]);
      setLoading(false);
      return;
    }
    if (!force && cache.has(email)) {
      setThemes(cache.get(email));
      setLoading(false);
      return;
    }
    if (force) inflight.delete(email);

    setLoading(true);
    setError("");
    try {
      if (!inflight.has(email)) inflight.set(email, getThemes(email));
      const data = await inflight.get(email);
      cache.set(email, data);
      setThemes(data);
    } catch (err) {
      console.error("Failed to load themes:", err);
      setError(err.message);
      setThemes([]);
    } finally {
      inflight.delete(email);
      setLoading(false);
    }
  }, [email]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onChange = (changed) => {
      if (changed !== email) return;
      setThemes(cache.get(email) || []);
    };
    listeners.add(onChange);
    return () => listeners.delete(onChange);
  }, [email]);

  return { themes, loading, error, refresh: () => load(true) };
}